import React from "react";
import { useTranslation } from "react-i18next";
import useIsMobile from "../../../../../../core/hooks/useMobile";
import { svgs } from "../../../../../../core/constants/svgs";
import { useQuestionsContext } from "../context/QuestionsContext";
import { useChapterItem } from "../hooks/useChapterItem";
import Question from "./question/Question";
import ChapterUpload from "./ChapterUpload";
import InlineChapterEditor from "./InlineChapterEditor";
import InlineSurveyDropdowns from "./InlineSurveyDropdowns";
import RichChapterEditor from "./RichChapterEditor";
import ChapterActionToolbar from "./ChapterActionToolbar";
import "./ChapterItem.css";

import editIcon from "../../../../../../assets/png/edit.png";
import checkIcon from "../../../../../../assets/png/check.png";
import addIcon from "../../../../../../assets/png/add.png";
import deleteIcon from "../../../../../../assets/png/delete-chapter.png";

const ChapterItem = ({
  item,
  index,
  activeCollapse,
  handleAccordion,
  dragDropHandlers,
  imageHandlers,
  chapterHandlers,
  questionHandlers,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    isPaid,
    general_category_id,
    category_based_type_id,
    category_length_type_id,
    isSelfWrite,
  } = useQuestionsContext();

  const {
    dragStartChapter,
    dragEnterChapter,
    handleDropChapter,
    dragStartQuestion,
    dragEnterQuestion,
    handleDropQuestion,
  } = dragDropHandlers;

  const {
    uploadImagesArr,
    otherPhotoInputId,
    otherPhotoImageId,
    onUploadImageClick,
    onUploadImageChange,
    onRemoveImage,
    onGenerateChapterImageWithAI,
    generatingChapterId,
  } = imageHandlers;

  const {
    onClickOpenDeleteChapterModal,
    onSubmitChapterClick,
    onChangeTextArea,
    onUpdateChapter,
    onAddChapter,
  } = chapterHandlers;

  const { onResult, onEditDeleteQuestion, onUpdateQuestion, onAddQuestion } =
    questionHandlers;

  const {
    isEditingTitle,
    editedTitle,
    showAddEditor,
    showQuestions,
    questionCount,
    handleEditChapter,
    handleTitleChange,
    handleTitleSubmit,
    handleAddChapterClick,
    handleInlineSubmit,
    handleInlineCancel,
    toggleQuestionsVisibility,
  } = useChapterItem(item, index, chapterHandlers);

  return (
    <>
      <div id={"container-flush-collapse-" + index} key={"question-" + index}>
        <div className="chapter-item-container">
          <div className="aai-accordions">
            <div className="accordion accordion-flush">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <div
                    name={"flush-collapse-" + index}
                    className={`accordion-button flex-wrap ${
                      isEditingTitle ? "editing-mode" : ""
                    }`}
                    type="button"
                    onClick={
                      !isEditingTitle && !showAddEditor
                        ? handleAccordion
                        : undefined
                    }
                  >
                    {!isMobile &&
                      !isEditingTitle &&
                      (isPaid || index === 0) && (
                        <img
                          src={svgs.draggable}
                          alt="draggable"
                          width={24}
                          style={{ cursor: "move" }}
                          onDragStart={(e) =>
                            isPaid &&
                            !isEditingTitle &&
                            !showAddEditor &&
                            dragStartChapter(e, index)
                          }
                          onDragEnter={(e) =>
                            isPaid &&
                            !isEditingTitle &&
                            !showAddEditor &&
                            dragEnterChapter(e, index)
                          }
                          onDragEnd={
                            isPaid && !isEditingTitle && !showAddEditor
                              ? handleDropChapter
                              : undefined
                          }
                          draggable={
                            isPaid && !isEditingTitle && !showAddEditor
                          }
                        />
                      )}
                    {!isPaid && index !== 0 && !isMobile && !isEditingTitle && (
                      <i className="fa-solid fa-lock chapter-lock-icon"></i>
                    )}

                    {/* True Inline Title Editing */}
                    <div
                      className={`chapter-title-section ${
                        isMobile ? "" : "pe-4"
                      } top_question_${index}`}
                      style={{
                        cursor: "text",
                        marginRight: isMobile ? "1rem" : "unset",
                        color: isPaid || index === 0 ? "black" : "#A69B8A",
                        flex: 1,
                        padding: isEditingTitle ? "8px 12px" : "0",
                        userSelect: isEditingTitle ? "none" : "text",
                      }}
                      onMouseDown={(e) => {
                        // Allow text selection by stopping propagation
                        if (!isEditingTitle) {
                          e.stopPropagation();
                        }
                      }}
                      onClick={(e) => {
                        // Prevent accordion toggle when clicking on title text
                        if (!isEditingTitle) {
                          e.stopPropagation();
                        }
                      }}
                    >
                      {isEditingTitle ? (
                        <div className="inline-edit-container">
                          <span className="chapter-number">
                            {t("chapter") + " " + (index + 1) + ": "}
                          </span>
                          <input
                            type="text"
                            className="form-control chapter-title-input"
                            value={editedTitle}
                            onChange={handleTitleChange}
                            onBlur={handleTitleSubmit}
                            autoFocus
                          />
                          <button
                            className="btn btn-sm"
                            onClick={handleTitleSubmit}
                          >
                            <img
                              src={checkIcon}
                              alt="Check"
                              width={24}
                              height={24}
                            />
                          </button>
                        </div>
                      ) : (
                        <div className="chapter-title-with-badge">
                          <span className="chapter-title-text">
                            {t("chapter") + " " + (index + 1) + ": "}
                            {item?.name}
                          </span>
                          {/* Free Chapter Badge - Now inside title section */}
                          {index === 0 &&
                            !isPaid &&
                            !isEditingTitle &&
                            !showAddEditor && (
                              <div
                                className="free-chapter-badge"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <p className="first-chapter-free">
                                  {t("first_chapter_is_free")}
                                </p>
                              </div>
                            )}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons - Stay on the right */}
                    {!isEditingTitle &&
                      !showAddEditor &&
                      (index === 0 || isPaid) && (
                        <div className="chapter-actions">
                          {/* Edit */}
                          <button
                            className={`chapter-action-bt me-2`}
                            onClick={handleEditChapter}
                            title={t("edit_chapter")}
                          >
                            <img
                              src={editIcon}
                              alt="Edit"
                              width={24}
                              height={24}
                              className="action-icon"
                            />
                          </button>
                          {/* Add */}
                          {isPaid && (
                            <button
                              className={`chapter-action-bt me-2`}
                              onClick={handleAddChapterClick}
                              title={t("add_chapter")}
                            >
                              <img
                                src={addIcon}
                                alt="Add"
                                width={24}
                                height={24}
                                className="action-icon"
                              />
                            </button>
                          )}
                          {/* Delete */}
                          <button
                            className={`chapter-action-bt me-2`}
                            onClick={onClickOpenDeleteChapterModal(index)}
                            title={t("delete_chapter")}
                          >
                            <img
                              src={deleteIcon}
                              alt="Delete"
                              width={24}
                              height={24}
                              className="action-icon"
                            />
                          </button>
                        </div>
                      )}
                  </div>
                </h2>

                {/* Accordion Content - Only show when not editing and collapsed state allows */}
                <div
                  id={"flush-collapse-" + index}
                  className="accordion-collapse"
                  style={{
                    position: "relative",
                    display: isEditingTitle || showAddEditor ? "none" : "block",
                  }}
                >
                  <div
                    className="accordion-body"
                    style={{
                      padding: "0px",
                      filter: index === 0 || isPaid ? "" : "blur(3px)",
                      userSelect: index === 0 || isPaid ? "" : "none",
                      pointerEvents: index === 0 || isPaid ? "" : "none",
                    }}
                  >
                    {/* Questions Toggle Section */}
                    {questionCount > 0 && (
                      <div className="questions-toggle-section">
                        <div
                          className="questions-toggle-header"
                          style={{
                            padding: "12px 16px",
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            marginBottom: showQuestions ? "16px" : "0px",
                            textAlign: "center",
                          }}
                        >
                          {!isSelfWrite && (
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#6c757d",
                                fontWeight: "500",
                                marginBottom: "8px",
                              }}
                            >
                              {t("chapter_generation_help", {
                                count: questionCount,
                              })}
                            </div>
                          )}
                          <div
                            onClick={toggleQuestionsVisibility}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              cursor: "pointer",
                              color: "#c47a26",
                              fontSize: "13px",
                              fontWeight: "500",
                              textDecoration: "none",
                              transition: "color 0.2s ease",
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.color = "#c47a26")
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.color = "#c47a26")
                            }
                          >
                            {isSelfWrite && (
                              <span style={{ marginRight: "4px", color: "#6c757d" }}>
                                {t("inspiring_questions")}
                              </span>
                            )}
                            <span>
                              {showQuestions ? t("collapse") : t("expand")}
                            </span>
                            <i
                              className={`fa-solid ${
                                showQuestions
                                  ? "fa-chevron-up"
                                  : "fa-chevron-down"
                              }`}
                              style={{
                                fontSize: "11px",
                                transition: "transform 0.2s ease",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Questions Section - Conditionally Rendered */}
                    {showQuestions &&
                      item?.questions?.map((question, q_index) => {
                        return (
                          <Question
                            key={"question-" + q_index}
                            onDragStart={(e) =>
                              isPaid && dragStartQuestion(e, q_index)
                            }
                            onDragEnter={(e) =>
                              isPaid && dragEnterQuestion(e, q_index)
                            }
                            onDragEnd={
                              isPaid ? handleDropQuestion(index) : undefined
                            }
                            draggable={isPaid}
                            question={question}
                            onResult={(str) => onResult(index, q_index, str)}
                            onEditDeleteQuestion={(type) => (event) =>
                              onEditDeleteQuestion(event, type, index, q_index)}
                            onUpdateQuestion={(questionData) =>
                              onUpdateQuestion(index, q_index, questionData)
                            }
                            onAddQuestion={() => onAddQuestion(index, q_index)}
                            isNewQuestion={question.isNewQuestion || false}
                            showAnswerHint={q_index === 0}
                          />
                        );
                      })}

                    {/* Chapter Upload Component - Hidden but not unmounted to preserve state */}
                    <div>
                      <ChapterUpload
                        item={item}
                        index={index}
                        uploadImagesArr={uploadImagesArr}
                        otherPhotoInputId={otherPhotoInputId}
                        otherPhotoImageId={otherPhotoImageId}
                        onUploadImageClick={onUploadImageClick}
                        onUploadImageChange={onUploadImageChange}
                        onRemoveImage={onRemoveImage}
                        onGenerateChapterImageWithAI={
                          onGenerateChapterImageWithAI
                        }
                        generatingChapterId={generatingChapterId}
                      />
                    </div>

                    {/* Show dropdowns above textarea when questions are expanded */}
                    {!isSelfWrite && showQuestions && item?.questions && (
                      <div className="chapter-navigation">
                        <div className="d-flex align-items-center">
                          <InlineSurveyDropdowns
                            onSubmit={onSubmitChapterClick(item, index)}
                            hasGeneratedContent={!!item?.generated_content}
                          />
                        </div>
                      </div>
                    )}

                    {!isSelfWrite && item?.generated_content && (
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#6c757d",
                          textAlign: "center",
                          margin: "8px auto 0",
                          maxWidth: "80%",
                        }}
                      >
                        {t("generated_text_edit_hint")}
                      </div>
                    )}

                    <div
                      className="aai-comment-input mt-3"
                      id={`generated_paragraph_${index}`}
                      style={{ display: "flex", justifyContent: "center" }}
                    >
                      {(isSelfWrite || item?.generated_content) && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                            width: "80%",
                            margin: "0px auto 20px auto",
                          }}
                        >
                          <ChapterActionToolbar
                            content={item?.generated_content ?? ""}
                            onContentChange={(newContent) =>
                              onChangeTextArea(index)({
                                target: { value: newContent },
                              })
                            }
                          />
                          <RichChapterEditor
                            content={item?.generated_content ?? ""}
                            onChange={(newContent) =>
                              onChangeTextArea(index)({
                                target: { value: newContent },
                              })
                            }
                            style={{ height: "500px", flex: 1, minWidth: 0 }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Show dropdowns below textarea when questions are collapsed */}
                    {!isSelfWrite && !showQuestions && item?.questions && (
                      <div className="chapter-navigation">
                        <div className="d-flex align-items-center">
                          <InlineSurveyDropdowns
                            onSubmit={onSubmitChapterClick(item, index)}
                            hasGeneratedContent={!!item?.generated_content}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inline Editor for Adding New Chapter - Keep this for adding new chapters */}
      {showAddEditor && (
        <InlineChapterEditor
          isVisible={showAddEditor}
          chapterToEdit={null}
          onSubmit={handleInlineSubmit}
          onCancel={handleInlineCancel}
          general_category_id={general_category_id}
          category_based_type_id={category_based_type_id}
          category_length_type_id={category_length_type_id}
        />
      )}
    </>
  );
};

export default React.memo(ChapterItem);
